import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Blogs.css';

const Blogs = () => {
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);

    // Scroll to top on page load
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Fetch blogs from API
    useEffect(() => {
        const fetchBlogs = async (page = 1) => {
            try {
                setLoading(true);
                const apiUrl = `https://winchelmohandes-furniture.online/api/blog?page=${page}`;

                // Add timeout to prevent hanging
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

                const response = await fetch(apiUrl, {
                    mode: 'cors',
                    signal: controller.signal,
                    headers: {
                        'Accept': 'application/json',
                    }
                });

                clearTimeout(timeoutId);
                console.log('Response status:', response.data);

                if (!response.ok) {
                    throw new Error(`فشل في تحميل المقالات (${response.status})`);
                }

                const data = await response.json();

                if (data.success) {
                    setBlogs(data.data.filter(blog => blog.category_id === 7) || []);
                    setPagination(data.pagination || null);
                } else {
                    throw new Error('فشل في تحميل المقالات');
                }
            } catch (err) {
                if (err.name === 'AbortError') {
                    setError('انتهت مهلة الطلب. يرجى التحقق من الاتصال بالإنترنت والمحاولة مرة أخرى.');
                } else {
                    setError(err.message || 'فشل في تحميل المقالات');
                }
                console.error('Error fetching blogs:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchBlogs(currentPage);
    }, [currentPage]);

    // Format date
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('ar-EG', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // Parse tags
    const parseTags = (tagsString) => {
        try {
            return JSON.parse(tagsString || '[]');
        } catch {
            return [];
        }
    };

    // Get full image URL
    const getImageUrl = (imagePath) => {
        if (!imagePath) return '';
        if (imagePath.startsWith('http')) return imagePath;
        return `https://winchelmohandes-furniture.online/api${imagePath}`;
    };

    // Handle page change
    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Generate page numbers for pagination
    const getPageNumbers = () => {
        if (!pagination) return [];

        const { current_page, last_page } = pagination;
        const pages = [];
        const maxVisible = 5; // Maximum number of page buttons to show

        if (last_page <= maxVisible) {
            // Show all pages if total pages is less than max visible
            for (let i = 1; i <= last_page; i++) {
                pages.push(i);
            }
        } else {
            // Always show first page
            pages.push(1);

            // Calculate range around current page
            let start = Math.max(2, current_page - 1);
            let end = Math.min(last_page - 1, current_page + 1);

            // Add ellipsis after first page if needed
            if (start > 2) {
                pages.push('...');
            }

            // Add pages around current page
            for (let i = start; i <= end; i++) {
                pages.push(i);
            }

            // Add ellipsis before last page if needed
            if (end < last_page - 1) {
                pages.push('...');
            }

            // Always show last page
            pages.push(last_page);
        }

        return pages;
    };

    return (
        <div className="blogs-page">
            <div className="page-header">
                <div className="header-overlay"></div>
                <div className="container">
                    <div className="header-content">
                        <span className="header-badge">
                            <i className="fas fa-blog"></i>
                            {pagination ? `${blogs.length} مقال` : 'المدونة'}
                        </span>
                        <h1>المدونة</h1>
                        <p>مقالات ونصائح حول التشطيبات والديكور</p>
                    </div>
                </div>
            </div>

            <section className="blogs-section">
                <div className="container">
                    {loading ? (
                        <div className="loading-state">
                            <div className="loading-spinner">
                                <i className="fas fa-spinner fa-spin"></i>
                            </div>
                            <p>جاري تحميل المقالات...</p>
                        </div>
                    ) : error ? (
                        <div className="error-state">
                            <div className="error-icon">
                                <i className="fas fa-exclamation-triangle"></i>
                            </div>
                            <h3>حدث خطأ</h3>
                            <p>{error}</p>
                            <button
                                className="retry-btn"
                                onClick={() => window.location.reload()}
                            >
                                إعادة المحاولة
                            </button>
                        </div>
                    ) : blogs.length === 0 ? (
                        <div className="no-blogs">
                            <div className="no-blogs-icon">
                                <i className="fas fa-file-alt"></i>
                            </div>
                            <h3>لا توجد مقالات متاحة</h3>
                            <p>لم يتم العثور على أي مقالات في الوقت الحالي</p>
                        </div>
                    ) : (
                        <>
                            {pagination && (
                                <div className="blogs-info">
                                    <div className="info-item">
                                        <i className="fas fa-file-alt"></i>
                                        <span>عرض {blogs.length} مقال</span>
                                    </div>
                                </div>
                            )}

                            <div className="blogs-grid">
                                {blogs.map((blog, index) => {
                                    const tags = parseTags(blog.tags);

                                    return (
                                        <article
                                            className="blog-card"
                                            key={blog.id}
                                            style={{ animationDelay: `${index * 0.1}s` }}
                                        >
                                            <div className="card-image-wrapper">
                                                <img
                                                    src={getImageUrl(blog.image_path)}
                                                    alt={blog.title}
                                                    onError={(e) => {
                                                        e.target.src = 'https://via.placeholder.com/400x250?text=No+Image';
                                                    }}
                                                />
                                                <div className="image-overlay"></div>
                                                <div className="category-badge">
                                                    <i className="fas fa-tag"></i>
                                                    {blog.category}
                                                </div>
                                            </div>

                                            <div className="card-content">
                                                <div className="card-header">
                                                    <h3>{blog.title}</h3>
                                                    <div className="blog-meta">
                                                        <span className="meta-item">
                                                            <i className="fas fa-user"></i>
                                                            {blog.author}
                                                        </span>
                                                        <span className="meta-item">
                                                            <i className="fas fa-calendar"></i>
                                                            {formatDate(blog.published_at)}
                                                        </span>
                                                    </div>
                                                </div>

                                                <p className="card-excerpt">{blog.excerpt}</p>

                                                {tags.length > 0 && (
                                                    <div className="card-tags">
                                                        {tags.map((tag, tagIndex) => (
                                                            <span key={tagIndex} className="tag">
                                                                #{tag}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}

                                                <div className="card-footer">
                                                    <Link
                                                        to={`/blogs/${blog.slug}`}
                                                        className="read-more-btn"
                                                    >
                                                        اقرأ المزيد
                                                        <i className="fas fa-arrow-left"></i>
                                                    </Link>
                                                </div>
                                            </div>
                                        </article>
                                    );
                                })}
                            </div>

                            {/* Pagination Controls */}
                            {pagination && pagination.last_page > 1 && (
                                <div className="pagination-wrapper">
                                    <div className="pagination">
                                        {/* Previous Button */}
                                        <button
                                            className={`pagination-btn pagination-prev ${currentPage === 1 ? 'disabled' : ''}`}
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                        >
                                            <i className="fas fa-chevron-right"></i>
                                            <span>السابق</span>
                                        </button>

                                        {/* Page Numbers */}
                                        <div className="pagination-numbers">
                                            {getPageNumbers().map((page, index) => (
                                                page === '...' ? (
                                                    <span key={`ellipsis-${index}`} className="pagination-ellipsis">
                                                        ...
                                                    </span>
                                                ) : (
                                                    <button
                                                        key={page}
                                                        className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                                                        onClick={() => handlePageChange(page)}
                                                    >
                                                        {page}
                                                    </button>
                                                )
                                            ))}
                                        </div>

                                        {/* Next Button */}
                                        <button
                                            className={`pagination-btn pagination-next ${currentPage === pagination.last_page ? 'disabled' : ''}`}
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === pagination.last_page}
                                        >
                                            <span>التالي</span>
                                            <i className="fas fa-chevron-left"></i>
                                        </button>
                                    </div>

                                    {/* Pagination Info */}
                                    <div className="pagination-info">
                                        <span>
                                            صفحة {pagination.current_page} من {pagination.last_page}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </section>

            <section className="blogs-cta-section">
                <div className="container">
                    <div className="blogs-cta">
                        <h2>هل تريد معرفة المزيد؟</h2>
                        <p>تواصل معنا للحصول على استشارة مجانية حول مشروعك</p>
                        <Link to="/contact" className="btn btn-primary">تواصل معنا</Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Blogs;

